"use client";
import React from "react";
import { isDateInPast } from "@/lib/date";
import { Skeleton } from "@/components/ui/skeleton";

import { cn } from "@/lib/utils";
import { getCourseRunEndText } from "@/lib/course/utils/getCourseRunTitleSecondaryText";

import Icon from "@/components/icons";
import { CourseEnrollProps } from "@/components/CourseContent/EnrollButton/types";
import useCourseEnrollment from "../../hooks/useCourseEnrollment";
import { MIN_ENROLLMENT_COUNT_FOR_DISPLAY } from "@/components/CourseContent/CourseRunSelector/constants";
import CourseEnrollButton from "../../CourseEnrollButtons";
import AuditLink from "../../CourseEnrollButtons/AuditLink";
import MultipleStartDateModal from "@/components/CourseContent/ContentRefresh/MultipleStartDateModal";
import { useSingleCourseTrafficConfig } from "@/monarch/rules/singleCourseTraffic/useSingleCourseTraffic";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import CourseDateDisplay from "./CourseDateDisplay";
import SinglePricingCard from "@/components/GeoPricing/SinglePricingCard";
import useTranslations from "@/i18n/useTranslations";

function CourseEnrollBox({
  buttonClass = "",
  className = "",
  courseRun,
  course,
  seeMoreDatesHandler,
  name = "",
  location = "",
  auditTextClassName = "",
  showEndDate = true,
  auditLinkLocation = "",
  isInCollapsedBody = false,
  straightToCheckout,
  pricingData,
}: CourseEnrollProps) {
  const t = useTranslations("Course");
  const translate =
    t as unknown as import("@/lib/course/utils/getCourseInformation").CourseInfoTranslate;
  const {
    isUserEnrolledInRun,
    isCourseEnrollable,
    isPaidLeadLoading,
    isCourseArchived,
  } = useCourseEnrollment({
    courseRun,
    course,
  });
  const { showHeaderPrice, showHeaderPriceMobile, headerPriceLabel } =
    useSingleCourseTrafficConfig({ course });

  // Don't render price in CourseEnrollBox when in header, as CourseTaggedHeader controls it
  const isInHeader = location === "hero" || name === "hero-cta";

  const courseRunPrice =
    pricingData?.discountedPrice ??
    pricingData?.originalPrice ??
    getCoursePrice(courseRun);
  const enrollmentCount = course.enrollmentCount;

  const showEnrollmentCount =
    !isCourseArchived &&
    courseRun.isEnrollable &&
    enrollmentCount &&
    enrollmentCount >= MIN_ENROLLMENT_COUNT_FOR_DISPLAY;

  if (isPaidLeadLoading) {
    return (
      <div className="flex flex-row gap-3 items-center bg-putty-100 p-4 rounded-xl border-putty-400 border">
        <Skeleton role="status" className="h-11 w-56 rounded-none" />
      </div>
    );
  }
  //
  const enrollmentEnded = courseRun.enrollmentEnd
    ? isDateInPast(courseRun.enrollmentEnd)
    : false;

  const isRetiringSoon = course.customizations?.catalogPage?.retiringSoon;
  const retiringSoonText =
    course.customizations?.catalogPage?.retiringSoonTextOverwrite ||
    t("retiringSoon");
  const endText = getCourseRunEndText(courseRun, translate);

  return (
    <div className="flex flex-col gap-4">
      {isRetiringSoon && (
        <div className="flex md:justify-end gap-2 text-primary">
          <Icon type="warning" />
          <p className="m-0 text-black">{retiringSoonText}</p>
        </div>
      )}
      <div
        className={cn(
          "flex flex-col gap-4 items-center justify-between bg-putty-100 px-4 py-6 md:p-4 rounded-xl border-putty-400 border w-auto",
          className || "",
        )}
      >
        <CourseDateDisplay
          oneLineStartDate={true}
          isCourseArchived={isCourseArchived}
          isCourseEnrollable={isCourseEnrollable}
          courseRun={courseRun}
          seeMoreDatesHandler={seeMoreDatesHandler}
          endText={endText}
          showEndDate={showEndDate}
        />

        {isCourseEnrollable ? (
          <div className="flex items-center w-full md:w-auto">
            <CourseEnrollButton
              buttonClass={`self-center ${buttonClass}`}
              courseRun={courseRun}
              course={course}
              name={name}
              location={location}
              price={courseRunPrice}
              shouldDisplayLearningOptions
              straightToCheckout={straightToCheckout}
            />
          </div>
        ) : !isUserEnrolledInRun && enrollmentEnded ? (
          <span>{t("futureDatesToBeAnnounced")}</span>
        ) : null}
        {!isInHeader && (showHeaderPrice || showHeaderPriceMobile) && (
          <div
            className={cn(
              "items-center text-base",
              // Desktop logic
              showHeaderPrice ? "md:flex" : "md:hidden",
              // Mobile logic
              showHeaderPriceMobile ? "flex" : "hidden",
            )}
          >
            <span className="font-bold m-0">
              {headerPriceLabel || t("upgradeFor")} ${courseRunPrice}
            </span>
          </div>
        )}

        {showEnrollmentCount ? (
          <div
            className={`items-center text-base ${showHeaderPriceMobile ? "hidden" : "flex"} ${showHeaderPrice ? "md:hidden" : "md:flex"}`}
          >
            <span className="font-bold m-0">
              {enrollmentCount.toLocaleString()}
            </span>
            <span className="m-0">&nbsp;{t("learnersEnrolled")}</span>
          </div>
        ) : null}

        {seeMoreDatesHandler ? (
          <MultipleStartDateModal
            course={course}
            showEnrollmentCount={showEnrollmentCount}
          >
            <button
              type="button"
              className="flex flex-row gap-1 justify-end items-center"
            >
              <Icon type="calendar-checked" />
              <span className="underline underline-offset-2">
                {t("seeMoreDates")}
              </span>
            </button>
          </MultipleStartDateModal>
        ) : null}

        {/* Inline AuditLink - inside main container */}
        {isCourseEnrollable && auditLinkLocation === "inline" ? (
          <AuditLink
            courseRun={courseRun}
            course={course}
            className={auditTextClassName}
            variant="icon"
            name={name}
            location={location}
          />
        ) : null}
      </div>

      {pricingData?.geoPricing != null &&
        pricingData.geoPricing.heroCardBadges != null &&
        pricingData.checkoutGeoPricingEligibility?.eligible === true && (
          <SinglePricingCard
            pricingData={pricingData}
            location="course-detail-page"
          />
        )}

      {isCourseEnrollable && auditLinkLocation !== "inline" ? (
        <AuditLink
          courseRun={courseRun}
          course={course}
          className={`pb-1 ${auditTextClassName}`}
          variant="icon"
          name={name}
          location={location}
        />
      ) : null}
    </div>
  );
}

export default CourseEnrollBox;
